import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail, Lock, User as UserIcon, ArrowLeft, Gift, AlertCircle, RefreshCw, Landmark, HeartHandshake, Users, GraduationCap, Sparkles, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/contexts/AuthContext";
import WeKitLogo from "@/components/WeKitLogo";
import authHero from "@/assets/auth-hero.png.asset.json";
import wekitLogo from "@/assets/wekit-logo.png.asset.json";
import {
  getStoredReferralHandle,
  fetchReferrerCard,
  type ReferrerCard,
} from "@/lib/referral";
import { resolvePostAuthDestination } from "@/lib/returnTo";
import PageHead from "@/components/PageHead";
import { buildSignupCallback, getSignupCoupon, normalizeSignupCoupon, storeSignupCoupon } from "@/lib/signupCoupon";

// Returns the origin that OAuth should redirect back to. We always use the
// current window origin so sign-in returns the user to whichever origin they
// started on — preview, published, or localhost. The corresponding origins
// must be present in the Supabase auth redirect allow-list.
function getOAuthRedirectOrigin(): string {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}


const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"signin" | "signup">(() => new URLSearchParams(location.search).get("mode") === "signup" ? "signup" : "signin");
  const [refCard, setRefCard] = useState<ReferrerCard | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown> | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const debugRef = useRef<HTMLDivElement | null>(null);

  // Debug panel is admin-only. We gate it on an explicit opt-in (URL ?debug=1 or
  // localStorage.wekit_debug='1') because /auth runs before we have a session,
  // so we can't check the admin role directly. Admins set the flag once on
  // their own device; regular users never see the panel.
  const isDebugEnabled = (() => {
    if (typeof window === "undefined") return false;
    try {
      if (new URLSearchParams(window.location.search).get("debug") === "1") {
        try { localStorage.setItem("wekit_debug", "1"); } catch { /* ignore */ }
        return true;
      }
      return localStorage.getItem("wekit_debug") === "1";
    } catch {
      return false;
    }
  })();

  const [signIn, setSignIn] = useState({ email: "", password: "" });
  const [signUp, setSignUp] = useState({ full_name: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [couponCode, setCouponCode] = useState(() => getSignupCoupon(location.search));
  const [couponInputError, setCouponInputError] = useState<string | null>(null);
  // Set after a successful sign-up when email confirmation is required — no
  // session exists yet, so we hold the user on a "check your inbox" screen
  // instead of navigating them into a signed-out app.
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);


  // Auto-scroll debug panel into view whenever new debug info arrives
  useEffect(() => {
    if (debugInfo && showDebug && debugRef.current) {
      debugRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [debugInfo, showDebug]);

  // Capture OAuth callback errors that arrive as URL params (e.g. ?error=...&error_description=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const err = params.get("error") || hash.get("error");
    if (err) {
      const info = {
        source: "oauth_callback_url",
        error: err,
        error_code: params.get("error_code") || hash.get("error_code"),
        error_description:
          params.get("error_description") || hash.get("error_description"),
        full_query: window.location.search,
        full_hash: window.location.hash,
        captured_at: new Date().toISOString(),
        redirect_uri_used: window.location.origin,
      };
      setDebugInfo(info);
      setGoogleError(info.error_description || info.error || "OAuth callback returned an error.");
      if (isDebugEnabled) setShowDebug(true);
    }
  }, []);

  // Resolve a safe post-auth destination. We support both `?next=` (used by
  // /redeem and email links) and router `location.state.from` (used by
  // ProtectedRoute / PaidRoute redirects). `sanitizeReturnTo` enforces a
  // strict same-origin allowlist and strips forbidden routes like /payment
  // or /auth so a paid user is never pinned back to the paywall after
  // signing in.
  const nextParam = (() => {
    try {
      return new URLSearchParams(location.search).get("next");
    } catch {
      return null;
    }
  })();
  const rawFrom = (location.state as { from?: string })?.from ?? null;
  const from = resolvePostAuthDestination({ next: nextParam, from: rawFrom });
  const callbackUrl = buildSignupCallback(getOAuthRedirectOrigin(), from, couponCode);

  useEffect(() => {
    storeSignupCoupon(couponCode);
  }, [couponCode]);

  const prepareSignup = () => {
    if (couponCode.trim() && !normalizeSignupCoupon(couponCode)) {
      setCouponInputError("Use 3–32 letters, numbers, hyphens or underscores.");
      return false;
    }
    storeSignupCoupon(couponCode);
    setCouponInputError(null);
    return true;
  };

  useEffect(() => {
    const h = getStoredReferralHandle();
    if (!h) return;
    setTab("signup"); // referred friends are new users — bias toward signup
    fetchReferrerCard(h).then(setRefCard);
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, from, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email: signIn.email,
      password: signIn.password,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Welcome back.");
    // Demo principal always lands in Meeting Mode → Institution
    const uid = data.user?.id;
    if (uid) {
      const { data: prof } = await (supabase as any)
        .from("profiles")
        .select("is_demo_account")
        .eq("id", uid)
        .maybeSingle();
      if (prof?.is_demo_account) {
        navigate("/meeting", { replace: true });
        return;
      }
    }
    navigate(from, { replace: true });
  };


  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prepareSignup()) return;
    if (!signUp.full_name.trim()) {
      toast.error("Please enter your full name.");
      return;
    }
    if (signUp.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    const signupRedirect = callbackUrl;
    const email = signUp.email.trim();
    const { data, error } = await supabase.auth.signUp({
      email,
      password: signUp.password,
      options: {
        emailRedirectTo: signupRedirect,
        data: { full_name: signUp.full_name.trim() },
      },
    });
    setLoading(false);
    if (error) {
      // Supabase returns a clear message when HIBP flags a leaked password.
      toast.error(error.message);
      return;
    }
    // With email confirmation on, signUp returns no session. Show the
    // confirmation screen and let onAuthStateChange deliver the session
    // once the learner clicks the link in their inbox.
    if (!data.session) {
      setConfirmEmail(email);
      return;
    }
    toast.success("Your account is ready. Welcome to WeKIT™.");
    navigate(from, { replace: true });
  };

  const handleResendConfirmation = async () => {
    if (!confirmEmail) return;
    setResending(true);
    const signupRedirect = callbackUrl;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: confirmEmail,
      options: { emailRedirectTo: signupRedirect },
    });
    setResending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Confirmation email sent again.");
  };


  type OAuthProvider = "google" | "apple" | "lovable";

  const handleOAuth = async (provider: OAuthProvider) => {
    if (!prepareSignup()) return;
    const providerLabel = provider === "apple" ? "Apple" : provider === "lovable" ? "Lovable" : "Google";
    setLoading(true);
    setGoogleError(null);
    setDebugInfo(null);
    const redirectUri = getOAuthRedirectOrigin();
    const startedAt = new Date().toISOString();
    try {
      // Always return to the origin the user started on. The origin must be
      // present in the Supabase auth redirect allow-list.

      // IMPORTANT: redirect_uri MUST be a PUBLIC same-origin URL. For full-page
      // browser OAuth the wrapper returns after `window.location.href = ...`
      // *before* it can call `supabase.auth.setSession()`, so the Supabase
      // client relies on `detectSessionInUrl` on landing. If we land on a
      // protected route (e.g. /dashboard behind PaidRoute), the guard
      // redirects to /auth and the tokens in the URL fragment are discarded
      // before they can be exchanged — no `sb-*-auth-token` is ever written.
      //
      // Land on /auth: this page is public, and the `useEffect` on `user`
      // above navigates to the resolved `from` destination (which honours
      // `?next=` and `location.state.from`) once the session hydrates.
      const googleReturn = callbackUrl;
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: googleReturn,
      });
      if (result.error) {
        const errObj = result.error as unknown;
        const msg = errObj instanceof Error ? errObj.message : String(errObj);
        const friendly = /popup|blocked/i.test(msg)
          ? "Popup was blocked. Allow popups for this site and try again."
          : /cancel|closed/i.test(msg)
          ? "Sign-in was cancelled before it finished."
          : /network|fetch/i.test(msg)
          ? `Network error reaching ${providerLabel}. Check your connection and retry.`
          : `${providerLabel} sign-in failed: ${msg}`;
        setGoogleError(friendly);
        setDebugInfo({
          source: "lovable.auth.signInWithOAuth",
          provider,
          redirect_uri_used: redirectUri,
          started_at: startedAt,
          failed_at: new Date().toISOString(),
          error_message: msg,
          error_name: errObj instanceof Error ? errObj.name : typeof errObj,
          error_stack: errObj instanceof Error ? errObj.stack : undefined,
          error_raw: (() => {
            try { return JSON.parse(JSON.stringify(errObj)); } catch { return String(errObj); }
          })(),
          result_redirected: result.redirected,
          user_agent: navigator.userAgent,
          current_url: window.location.href,
        });
        if (isDebugEnabled) setShowDebug(true);
        toast.error(friendly);
        // Broker-level failures aren't recoverable inline — send the person to
        // a page that explains what happened and what to do next.
        if (/PROJECT_NOT_FOUND|server_error|broker/i.test(msg)) {
          const code = /PROJECT_NOT_FOUND/i.test(msg) ? "PROJECT_NOT_FOUND" : "server_error";
          navigate(`/auth/error?error=${code}&error_description=${encodeURIComponent(msg)}`);
        }
        setLoading(false);

        return;
      }
      if (result.redirected) return;
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unexpected error during sign-in.";
      setGoogleError(`Couldn't complete sign-in: ${msg}`);
      setDebugInfo({
        source: "exception",
        provider,
        redirect_uri_used: redirectUri,
        started_at: startedAt,
        failed_at: new Date().toISOString(),
        error_message: msg,
        error_name: err instanceof Error ? err.name : typeof err,
        error_stack: err instanceof Error ? err.stack : undefined,
        user_agent: navigator.userAgent,
        current_url: window.location.href,
      });
      if (isDebugEnabled) setShowDebug(true);
      toast.error(`${providerLabel} sign-in failed`);
      setLoading(false);
    }
  };

  const copyDebug = async () => {
    if (!debugInfo) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
      toast.success("Debug info copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  };

  if (confirmEmail) {
    return (
      <div className="relative min-h-screen bg-background flex flex-col overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary/15 blur-[120px]"
        />
        <header className="relative px-6 py-4 flex items-center justify-between border-b border-border/50">
          <Link to="/" className="flex items-center gap-3">
            <WeKitLogo size="sm" />
            <span className="font-display text-lg font-semibold">WeKIT™</span>
          </Link>
        </header>
        <main className="relative flex-1 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <Card className="glass-card border-0 shadow-2xl shadow-primary/5">
              <CardHeader className="text-center space-y-3">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15">
                  <Mail className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <CardTitle className="font-display text-2xl">Check your inbox</CardTitle>
                <CardDescription>
                  We sent a confirmation link to{" "}
                  <span className="font-medium text-foreground">{confirmEmail}</span>. Click it to
                  activate your account — then you're in.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Can't find it? Look in spam or promotions. The link expires after a while, so
                  we can send a fresh one.
                </p>
                <Button
                  onClick={handleResendConfirmation}
                  disabled={resending}
                  className="w-full min-h-12"
                >
                  {resending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" /> Resend confirmation email
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full min-h-12"
                  onClick={() => {
                    setConfirmEmail(null);
                    setTab("signin");
                  }}
                >
                  Use a different email
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </main>
      </div>
    );
  }


  const BRAND = "#00665B";
  const BRAND_SOFT = "#B7EBDC";

  return (
    <div className="auth-surface min-h-screen w-full flex bg-background text-foreground">
      <PageHead
        title="Sign in or create your account | WeKIT™ Career Clarity 360"
        description="Sign in to WeKIT™ Career Clarity 360 to continue your career journey, or create a free account to start discovering careers that fit you."
        path="/auth"
      />

      {/* LEFT — hero panel */}
      <aside className="hidden lg:flex relative w-1/2 overflow-hidden">
        <img
          src={authHero.url}
          alt="Student imagining diverse career futures — scientist, pilot, developer, photographer, executive, doctor, architect"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(140deg, rgba(0,102,91,0.55) 0%, rgba(0,40,36,0.85) 100%)" }}
        />
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 text-white w-full">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <img src={wekitLogo.url} alt="WeKIT" className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur p-1" />
              <span className="font-display text-lg font-semibold tracking-tight">
                WeKIT<span className="text-white/70 align-super text-[10px]">™</span>
              </span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-1.5 text-white/85 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          </div>

          <div className="max-w-lg">
            <div className="inline-block rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[11px] font-semibold tracking-[0.18em] uppercase mb-6">
              Career Clarity 360
            </div>
            <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">
              Discover your<br />Career DNA.
            </h1>
            <p className="mt-5 text-white/85 text-base xl:text-lg leading-relaxed max-w-md">
              Explore your strengths, discover possibilities and build your next steps with WeKIT™.
            </p>
          </div>

          <div className="flex items-center gap-3 text-sm text-white/85">
            <div className="flex -space-x-2" aria-hidden="true">
              <span className="w-8 h-8 rounded-full ring-2 ring-white/70 bg-gradient-to-br from-emerald-300 to-teal-500" />
              <span className="w-8 h-8 rounded-full ring-2 ring-white/70 bg-gradient-to-br from-amber-200 to-orange-400" />
              <span className="w-8 h-8 rounded-full ring-2 ring-white/70 bg-gradient-to-br from-sky-200 to-indigo-500" />
            </div>
            <span>Your potential. Your possibilities. Your next step.</span>
          </div>
        </div>
      </aside>

      {/* RIGHT — auth panel */}
      <main className="flex-1 flex items-center justify-center px-5 sm:px-10 py-10 min-h-screen overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-6 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <WeKitLogo size="sm" />
              <span className="font-display text-base font-semibold text-neutral-900">WeKIT™</span>
            </Link>
            <Link to="/" className="text-sm text-neutral-600 inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>

          {refCard && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-5 rounded-2xl border p-3 flex items-center gap-3"
              style={{ borderColor: `${BRAND}33`, background: `${BRAND}0D` }}
            >
              <div
                className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${BRAND}1A` }}
              >
                <Gift className="h-4 w-4" style={{ color: BRAND }} />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-neutral-900">
                  {refCard.firstName}'s ₹100 gift is waiting
                </p>
                <p className="text-xs text-neutral-600">
                  Finish signup to claim your discount automatically.
                </p>
              </div>
            </motion.div>
          )}

          <h2 className="font-display text-3xl font-semibold tracking-tight text-neutral-900">
            {tab === "signup" ? "Create your WeKIT™ account" : "Welcome back to WeKIT™"}
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            {tab === "signup" ? "Begin your Career Clarity 360 journey." : "Continue your Career Clarity 360 journey."}
          </p>

          <Tabs value={tab} onValueChange={(value) => setTab(value as "signin" | "signup")} className="mt-5">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup">Sign up</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="mt-4 space-y-2">
            <Label htmlFor="signup-coupon">Coupon or invitation code (optional)</Label>
            <Input id="signup-coupon" value={couponCode} maxLength={32}
              placeholder="Enter your code" disabled={loading}
              onChange={(event) => { setCouponCode(event.target.value.toUpperCase()); setCouponInputError(null); }}
              aria-invalid={!!couponInputError} aria-describedby="signup-coupon-note" />
            <p id="signup-coupon-note" role={couponInputError ? "alert" : undefined} className={couponInputError ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
              {couponInputError ?? "Your code will be checked at checkout before you pay."}
            </p>
          </div>

          {/* PRIMARY — Google sign-in for students & youth */}
          <div className="mt-6">
            <Button
              type="button"
              variant="outline"
              data-testid="google-signin-button"
              aria-label="Continue with Google"
              className="w-full h-12 rounded-xl bg-white hover:bg-neutral-50 border-neutral-200 text-base font-semibold text-neutral-900 hover:text-neutral-900 shadow-sm hover:shadow-md"
              style={{ boxShadow: `0 0 0 1px ${BRAND}22` }}
              onClick={() => handleOAuth("google")}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Continue with Google
            </Button>
            <Button
              type="button"
              variant="outline"
              aria-label="Continue with Apple"
              className="mt-3 w-full h-12 rounded-xl bg-white hover:bg-neutral-50 border-neutral-200 text-base font-semibold text-neutral-900 hover:text-neutral-900 shadow-sm hover:shadow-md"
              onClick={() => handleOAuth("apple")}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-5 h-5 mr-2 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M16.365 1.43c0 1.14-.42 2.2-1.26 3.02-.9.9-1.98 1.42-3.12 1.33-.03-1.1.44-2.2 1.25-3 .84-.83 2.06-1.4 3.13-1.35zM20.5 17.02c-.55 1.27-.81 1.84-1.52 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.93-1-4.02-.99-2.09.01-2.52 1.01-4.06.99-1.73-.01-3.05-1.76-4.04-3.32C.02 15.8-.27 10.7 1.44 8.02c1.2-1.9 3.1-3.01 4.89-3.01 1.82 0 2.97 1 4.48 1 1.46 0 2.35-1 4.46-1 1.6 0 3.29.87 4.5 2.38-3.95 2.17-3.31 7.82 1.23 9.63z"/>
                </svg>
              )}
              Continue with Apple
            </Button>
            <Button type="button" variant="outline" className="mt-3 w-full min-h-12" disabled={loading} onClick={() => handleOAuth("lovable")}>
              Continue with Lovable
            </Button>
          </div>

          {googleError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Sign-in failed</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>{googleError}</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleOAuth("google")}
                  disabled={loading}
                  className="mt-1"
                >
                  {loading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isDebugEnabled && (
            <div
              ref={debugRef}
              className="mt-4 rounded-md border border-neutral-200 bg-white/70 p-3 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono text-neutral-500 truncate">
                  redirect_uri: <span className="text-neutral-900">{getOAuthRedirectOrigin()}</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowDebug((v) => !v)}
                >
                  {showDebug ? "Hide debug" : "Show debug"}
                </Button>
              </div>
              {showDebug && (
                <div className="mt-2 space-y-2">
                  {debugInfo ? (
                    <>
                      <pre className="max-h-64 overflow-auto rounded bg-neutral-50 p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap break-all">
{JSON.stringify(debugInfo, null, 2)}
                      </pre>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={copyDebug}
                      >
                        Copy debug JSON
                      </Button>
                    </>
                  ) : (
                    <p className="text-neutral-500">No OAuth error captured yet.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex-1 border-t border-border" />Or continue with email<span className="flex-1 border-t border-border" />
          </div>
          <Tabs value={tab} onValueChange={(v) => setTab(v as "signin" | "signup")}>
            <TabsContent value="signin" className="mt-5">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="si-email" className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                    Email address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" aria-hidden="true" />
                    <Input
                      id="si-email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="pl-9 h-12 rounded-xl bg-white border-neutral-200 text-neutral-900"
                      value={signIn.email}
                      onChange={(e) => setSignIn({ ...signIn, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="si-pwd" className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                    Secure password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" aria-hidden="true" />
                    <Input
                      id="si-pwd"
                      type={showPwd ? "text" : "password"}
                      required
                      className="pl-9 pr-10 h-12 rounded-xl bg-white border-neutral-200 text-neutral-900"
                      value={signIn.password}
                      onChange={(e) => setSignIn({ ...signIn, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="font-medium hover:underline"
                    style={{ color: BRAND }}
                    onClick={async () => {
                      if (!signIn.email) {
                        toast.error("Enter your email above first.");
                        return;
                      }
                      const { error } = await supabase.auth.resetPasswordForEmail(signIn.email, {
                        redirectTo: `${getOAuthRedirectOrigin()}/reset-password`,
                      });
                      if (error) toast.error(error.message);
                      else toast.success("Check your email for a reset link.");
                    }}
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl text-base font-semibold text-white hover:opacity-95 transition-opacity"
                  style={{ background: BRAND }}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Sign in <span aria-hidden className="ml-2">→</span>
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-5">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="su-name" className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                    Full name
                  </Label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" aria-hidden="true" />
                    <Input
                      id="su-name"
                      required
                      className="pl-9 h-12 rounded-xl bg-white border-neutral-200 text-neutral-900"
                      value={signUp.full_name}
                      onChange={(e) => setSignUp({ ...signUp, full_name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-email" className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                    Email address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" aria-hidden="true" />
                    <Input
                      id="su-email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="pl-9 h-12 rounded-xl bg-white border-neutral-200 text-neutral-900"
                      value={signUp.email}
                      onChange={(e) => setSignUp({ ...signUp, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="su-pwd" className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                    Secure password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" aria-hidden="true" />
                    <Input
                      id="su-pwd"
                      type={showPwd ? "text" : "password"}
                      required
                      minLength={8}
                      className="pl-9 pr-10 h-12 rounded-xl bg-white border-neutral-200 text-neutral-900"
                      value={signUp.password}
                      onChange={(e) => setSignUp({ ...signUp, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-neutral-500">Minimum 8 characters</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl text-base font-semibold text-white hover:opacity-95 transition-opacity"
                  style={{ background: BRAND }}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Create account <span aria-hidden className="ml-2">→</span>
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-xs text-neutral-500 mt-6">
            Your journey starts with you.{" "}
            <Link to="/faqs" className="font-medium hover:underline" style={{ color: BRAND }}>
              Contact Help Center
            </Link>
          </p>
          <p className="text-center text-[11px] text-neutral-400 mt-2">
            © {new Date().getFullYear()} WeKIT™. Career Clarity 360.
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Auth;

